/**
 * Item filtering and sorting utilities
 * Used by LootBrowser and ConsumableBrowser
 */

import { LootItem, ConsumableItem, AnyItem } from '../types/equipment';

export function filterBySubtype<T extends AnyItem>(items: T[], subtype: string | null): T[] {
  if (!subtype || subtype === 'all') return items;
  return items.filter(item => item.subtype === subtype);
}

export function filterOfficial<T extends AnyItem>(items: T[], official: boolean): T[] {
  if (!official) return items;
  return items.filter(item => item.metadata?.official === true);
}

export function searchByText<T extends AnyItem>(items: T[], query: string): T[] {
  if (!query || query.trim() === '') return items;

  const lowerQuery = query.toLowerCase().trim();

  return items.filter(item => {
    // Search in name
    if (item.name.toLowerCase().includes(lowerQuery)) return true;

    // Search in description
    if (item.description && item.description.toLowerCase().includes(lowerQuery)) return true;

    // Search in feature
    if (item.feature && item.feature.toLowerCase().includes(lowerQuery)) return true;

    // Search in tags
    if (item.tags && item.tags.some(tag => tag.toLowerCase().includes(lowerQuery))) return true;

    return false;
  });
}

export function sortItems<T extends AnyItem>(items: T[], sortBy: 'name' | 'subtype'): T[] {
  const sorted = [...items];

  switch (sortBy) {
    case 'name':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));

    case 'subtype':
      return sorted.sort((a, b) => {
        const subtypeCompare = a.subtype.localeCompare(b.subtype);
        if (subtypeCompare !== 0) return subtypeCompare;
        // Secondary sort by name
        return a.name.localeCompare(b.name);
      });

    default:
      return sorted;
  }
}

/**
 * Apply all filters and sorting to a list of items
 */
export function applyFiltersAndSort<T extends AnyItem>(
  items: T[],
  filters: {
    subtype: string | null;
    official: boolean;
    search: string;
    sort: 'name' | 'subtype';
  }
): T[] {
  let filtered = items;

  filtered = filterBySubtype(filtered, filters.subtype);
  filtered = filterOfficial(filtered, filters.official);
  filtered = searchByText(filtered, filters.search);
  filtered = sortItems(filtered, filters.sort);

  return filtered;
}
